const PORTAL_SESSION_TOKEN_KEY = "lysara_active_login_session_token";

/**
 * The portal lease must outlive a browser tab so a persisted Supabase session
 * can resume its portal after the browser is closed and reopened. Migrate the
 * old sessionStorage value the next time the app reads it.
 */
export function readPortalSessionToken(): string | null {
  if (typeof window === "undefined") return null;

  let persistentToken: string | null = null;
  try {
    persistentToken = window.localStorage.getItem(PORTAL_SESSION_TOKEN_KEY);
  } catch {
    // Fall back to the legacy tab-scoped value when persistent storage is unavailable.
  }

  if (persistentToken) {
    try { window.sessionStorage.removeItem(PORTAL_SESSION_TOKEN_KEY); } catch { /* ignore */ }
    return persistentToken;
  }

  let legacyToken: string | null = null;
  try {
    legacyToken = window.sessionStorage.getItem(PORTAL_SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
  if (!legacyToken) return null;

  try {
    window.localStorage.setItem(PORTAL_SESSION_TOKEN_KEY, legacyToken);
    window.sessionStorage.removeItem(PORTAL_SESSION_TOKEN_KEY);
  } catch {
    // Keep the legacy value available for this tab if migration cannot complete.
  }
  return legacyToken;
}

/** Store the server-issued lease in persistent browser storage. */
export function writePortalSessionToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PORTAL_SESSION_TOKEN_KEY, token);
    try { window.sessionStorage.removeItem(PORTAL_SESSION_TOKEN_KEY); } catch { /* ignore */ }
  } catch {
    // Keep the portal usable in browsers where persistent storage is blocked.
    try { window.sessionStorage.setItem(PORTAL_SESSION_TOKEN_KEY, token); } catch { /* unavailable storage */ }
  }
}

/** Remove both the persistent token and any value left by the previous build. */
export function clearPortalSessionToken(): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(PORTAL_SESSION_TOKEN_KEY); } catch { /* ignore */ }
  try { window.sessionStorage.removeItem(PORTAL_SESSION_TOKEN_KEY); } catch { /* ignore */ }
}
